import * as React from 'react';
import { Input, InputProps, Label, FormFeedback } from 'reactstrap';

import { observer } from 'mobx-react';
import { InputType } from 'reactstrap/lib/Input';
import { BaseFormModel } from '@app/Services/BaseFormModel';
import { SelectFilterItem } from '@app/Models/SelectFilterItem';

export interface IFormCheckboxProps extends InputProps {
    formModel: BaseFormModel;
    label?: string;
    name: string;
    id?: string;
}

@observer
export class FormCheckbox extends React.Component<IFormCheckboxProps, {}> {
    public render() {
        const { label, name, id, formModel } = this.props;
        return (
            <React.Fragment>
                <Label className="checkbox">
                    <Input
                        type="checkbox"
                        checked={formModel[name]}
                        name={name}
                        invalid={formModel.validated ? !formModel.isValid(name) : formModel.validated}
                        onChange={ev => {
                            formModel[name] = ev.target.checked;
                        }}
                    />
                    <span>{label}</span>
                </Label>
                <FormFeedback>{formModel.errorFor(name)}</FormFeedback>
            </React.Fragment>
        );
    }
}

@observer
export class FormSwitcher extends React.Component<IFormCheckboxProps, {}> {
    public render() {
        const { name, formModel, disabled, changeHandler } = this.props;
        return (
            <React.Fragment>
                <Label className="switcher">
                    <Input
                        type="checkbox"
                        checked={formModel[name]}
                        name={name}
                        disabled={disabled}
                        invalid={formModel.validated ? !formModel.isValid(name) : formModel.validated}
                        onChange={ev => {
                            formModel[name] = ev.target.checked;
                            changeHandler?.();
                        }}
                    />
                    <span/>
                </Label>
                <FormFeedback>{formModel.errorFor(name)}</FormFeedback>
            </React.Fragment>
        );
    }
}

export interface IFormInputProps extends InputProps {
    formModel: BaseFormModel;
    name: string;
    placeholder?: string;
    type?: InputType;
    changeHandler?: Function;
}

@observer
export class FormInput extends React.Component<IFormInputProps, {}> {
    public render() {
        const { formModel, name, changeHandler, placeholder, bsSize, ...rest} = this.props;
        let type: InputType = this.props.type ? this.props.type : 'text';
        return (
            <React.Fragment>
                <Input
                    name={name}
                    value={formModel[name]}
                    invalid={formModel.validated ? !formModel.isValid(name) : formModel.validated}
                    onChange={ev => {
                        formModel.setValue(name, ev.target.value);
                        changeHandler?.();
                    }}
                    placeholder={placeholder}
                    bsSize={bsSize}
                    type={type}
                    {...rest}
                />
                <FormFeedback>{formModel.errorFor(name)}</FormFeedback>
            </React.Fragment>
        );
    }
}

export interface IFormSimpleSelectProps extends InputProps {
    formModel: BaseFormModel;
    name: string;
    options: string[];
    placeholder?: string;
}

@observer
export class FormSimpleSelect extends React.Component<IFormSimpleSelectProps, {}> {
    public render() {
        const { formModel, name, placeholder, options } = this.props;
        return (
            <React.Fragment>
                <Input
                    name={name}
                    value={formModel[name]}
                    invalid={formModel.validated ? !formModel.isValid(name) : formModel.validated}
                    onChange={ev => { formModel[name] = ev.target.value; }}
                    placeholder={placeholder}
                    type="select"
                >
                    <option value="" />
                    {options.map((t: string) => <option value={t} key={t}>{t}</option>)}
                </Input>
                <FormFeedback>{formModel.errorFor(name)}</FormFeedback>
            </React.Fragment>
        );
    }
}

export interface IFormSelectProps extends InputProps {
    formModel: BaseFormModel;
    name: string;
    options: (SelectFilterItem | string)[];
    hasEmpty?: boolean;
    placeholder?: string;
    customOptions?: JSX.Element[];
    changeHandler?: Function;
    customInputChangeHandler?: Function;
    customSelectChangeHandler?: Function;
    showField?: boolean;
    smallValidationError?: boolean;
}

@observer
export class FormSelect extends React.Component<IFormSelectProps, {}> {

    public render() {
        const { formModel, name, placeholder, options, customOptions, hasEmpty, changeHandler, smallValidationError, showField, customInputChangeHandler, customSelectChangeHandler, changeYearPeriodHandler, ...rest } = this.props;
        const { [name]: fieldValue } = formModel;
        const selectComponent = (
            <React.Fragment>
                <Input
                    name={name}
                    value={fieldValue}
                    invalid={formModel.validated ? !formModel.isValid(name) : formModel.validated}
                    onFocus={(event: React.ChangeEvent<HTMLElement>) => {
                        if (showField && options) {
                            const index = options.findIndex(opt => {
                                const val = typeof opt === 'string' ? opt : opt.value;
                                return val.toLowerCase() === String(fieldValue).toLowerCase();
                            });
                            const select = event.target as HTMLSelectElement;
                            select.selectedIndex = index;
                        }
                    }}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        formModel.setValue(name, event.target.value);
                        if (changeYearPeriodHandler) changeYearPeriodHandler();
                        if (customSelectChangeHandler) {
                            customSelectChangeHandler();
                        } else if (changeHandler) {
                            changeHandler();
                        }
                    }}
                    placeholder={placeholder}
                    type="select"
                    {...rest}>
                    {hasEmpty && <option value="" />}
                    {!customOptions &&
                        options.map((option: SelectFilterItem | string) => {
                            const value = (typeof option === 'string') ? option : option.value;
                            const label = (typeof option === 'string') ? option : option.label;
                            return <option value={value} key={value}>{label}</option>;
                        })}
                    {customOptions}
                </Input>
                {formModel.invalidFields.includes(name) &&
                    <FormFeedback className={smallValidationError ? 'label-small' : ''}>
                        {formModel.errorFor(name).map((error: string) => <div key={name + error}>{error}</div>)}
                    </FormFeedback>}
            </React.Fragment>
        );
        if (showField) {
            return (
                <div className="select-field">
                    <Input
                        name={name}
                        value={fieldValue}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            formModel.setValue(name, event.target.value);
                            if (customInputChangeHandler) {
                                customInputChangeHandler();
                            } else if (changeHandler) {
                                changeHandler();
                            }
                        }}
                        autoComplete="off"
                        {...rest} />
                    {selectComponent}
                </div>
            );
        } else {
            return selectComponent;
        }
    }
}
